import React, { useState, useCallback, useEffect, useRef } from 'react'

import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'

import ReactCreditCards from 'react-credit-cards'

import classNames from 'classnames'
import moment from 'moment'

import 'react-credit-cards/lib/styles.scss'
import { loadStripe } from '../services/Stripe'

import api, { CreditCard } from '../api'
import { JobInvoiceItem, calculateBillableAmount, calculateTotalBillableAmount, formatDuration, calculateTotalSeconds, calculateTotalBillableSeconds } from '../data/job_invoice_item'
import { useUserMeta } from '../hooks/useUserMeta'
import { freeTrialRemaining } from '../data/user'
import useTitle from '../hooks/useTitle'

function pad(nx: number, width: number, z?: string) {
  z = z || '0';
  const n = nx + '';
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}

interface CardFormElements extends HTMLFormControlsCollection {
  cardholderName: HTMLInputElement
}

const CardForm: React.FC<{updateCards: (cards: CreditCard[]) => void}> = ({ updateCards }) => {
  const stripe = useStripe()
  const elements = useElements()
  const formRef = useRef<HTMLFormElement>(null)
  const [ submitting, setSubmitting ] = useState<boolean>(false)

  const reset = useCallback(
    () => {
      const cardElement = elements?.getElement(CardElement)
      cardElement?.clear()

      formRef.current?.reset()
      setSubmitting(false)
    },
    [ elements, formRef ]
  )

  const handleNewCard = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      if (!stripe || !elements) return
      event.preventDefault()
      setSubmitting(true)

      const formElements = event.currentTarget.elements as CardFormElements
      const name = formElements.cardholderName.value

      const cardElement = elements.getElement(CardElement)
      const { error, token } = await stripe.createToken(cardElement!, {
        name
      })

      if (error) {
        alert(error.message)
        setSubmitting(false)
        return
      }

      if (!token) return

      try {
        const resp = await api.billing.addCard(token.id)
        if (resp.data.error === false) {
          updateCards(resp.data.data)
          reset()
        } else {
          console.error(resp.data)
        }
      } catch (error) {
        if (error.response) {
          const { error_message } = error.response.data
          alert(error_message)
          setSubmitting(false)
        } else {
          console.error('Error', error.message)
        }
      }
    },
    [ stripe, elements, reset, updateCards, setSubmitting ]
  )

  return (
    <form onSubmit={handleNewCard} ref={formRef}>
      <div className="my-2">
        <label className="sr-only" htmlFor="cardholderName">Cardholder Name</label>
        <input
          type="text"
          className="form-control mb-2 mr-sm-2"
          id="cardholderName"
          name="cardholderName"
          placeholder="Your Name Here"
          required={true}
        />
      </div>
      <div className="my-2">
        <CardElement />
      </div>
      <button
        className="btn btn-primary"
        type="submit"
        disabled={submitting}
      >
        Add Card
      </button>
    </form>
  )
}

const Card: React.FC<{card: CreditCard, updateCards: (cards: CreditCard[]) => void}> = ({ card, updateCards }) => {
  const setDefaultCard = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      const resp = await api.billing.setDefaultCard(card.id)
      if (resp.data.error === false) {
        updateCards(resp.data.data)
      }
    },
    [ card, updateCards ]
  )

  const buttonClass = classNames(
    "btn",
    {
      "btn-outline-primary": card.default_source,
      "btn-secondary": !card.default_source
    }
  )
  const buttonText = card.default_source ? "Default Credit Card" : "Set as default"

  let brand: string = card.brand
  if (brand === "American Express") {
    brand = 'amex'
  }

  let firstNumbers = "************"
  if (brand === 'amex') {
    firstNumbers = "***********"
  }

  return (
    <div className="col-lg-6 d-flex flex-column my-2" key={card.id}>
      <div className="text-center mb-1">
        <button
          className={buttonClass}
          disabled={card.default_source}
          onClick={setDefaultCard}>
          {buttonText}
        </button>
      </div>
      <ReactCreditCards
        preview={true}
        issuer={brand}
        number={firstNumbers + card.last4}
        expiry={`${pad(card.exp_month, 2)}${card.exp_year}`}
        name={card.name || ""}
        cvc={""}
      />
      { card.default_source && <div className="text-center mt-1">
        <small>This credit card will be used to bill you.</small>
      </div>}
    </div>
  )
}

const Invoice: React.FC<{invoice: JobInvoiceItem[]}> = ({invoice}) => {
  if (invoice.length === 0) {
    return (
      <p>
        You have not transcribed any files yet.
      </p>
    )
  }

  return (
    <table className="table table-bordered">
      <thead>
        <tr>
          <th>Date</th>
          <th>Job Name</th>
          <th>Minutes transcribed</th>
          <th>Amount to bill</th>
        </tr>
      </thead>

      <tbody>
        {invoice.map((li) => <tr key={li.sk}>
          <td>{moment(li.submitted_at).format("MMMM Do YYYY")}</td>
          <td>{li.job_name}</td>
          <td>{formatDuration(li.total_seconds)}</td>
          <td>{(calculateBillableAmount(li) / 100).toLocaleString("en-US", {style:"currency", currency:"USD"})}</td>
        </tr>)}
      </tbody>
    </table>
  )
}

interface BillingAmountDetailsProps {
  invoice: JobInvoiceItem[]
}
const BillingAmountDetails: React.FC<BillingAmountDetailsProps> = ({invoice}) => {
  const amountToBill = calculateTotalBillableAmount(invoice)
  const totalSeconds = calculateTotalSeconds(invoice)
  const billableSeconds = calculateTotalBillableSeconds(invoice)

  return <dl>
    <dt>Total time transcribed</dt>
    <dd>{formatDuration(totalSeconds)}</dd>
    { billableSeconds < totalSeconds && <>
      <dt>Billable time (time transcribed, excluding the free tier)</dt>
      <dd>{formatDuration(billableSeconds)}</dd>
    </> }
    <dt>Estimated bill for this month:</dt>
    <dd>{(amountToBill / 100).toLocaleString("en-US", {style:"currency", currency:"USD"})}</dd>
  </dl>
}

const Billing: React.FC = () => {
  useTitle("Billing | Speech2")

  const [ cards, setCards ] = useState<CreditCard[]>([])
  const [ invoice, setInvoice ] = useState<JobInvoiceItem[]>([])
  const userMeta = useUserMeta()

  useEffect(() => {
    (async () => {
      const resp = await api.billing.listCards()
      if (resp.data.error === false) {
        setCards(resp.data.data)
      }
    })();

    (async () => {
      const resp = await api.billing.invoice()
      if (resp.data.error === false) {
        setInvoice(resp.data.data)
      }
    })();
  }, [])

  const updateCards = useCallback(
    (cards: CreditCard[]) => {
      setCards(cards)
    },
    []
  )

  const remainingSecs = userMeta && freeTrialRemaining(userMeta)

  return (
    <div className="container">
      <h1 className="my-4">Billing Information</h1>

      <div className="row">
        {cards.map((card) => <Card key={card.id} card={card} updateCards={updateCards} />)}
      </div>

      <div className="row my-2">
        <div className="col-md-6">
          <h5>Add a new credit card</h5>

          <Elements stripe={loadStripe}>
            <CardForm updateCards={updateCards} />
          </Elements>
        </div>
      </div>

    <h3 className="my-4">Invoice for {moment().format('MMMM YYYY')}</h3>
      { remainingSecs ? <>
        <p>
          You have {formatDuration(remainingSecs)} left on your free trial.
        </p>
      </> : null }

      { invoice ? <BillingAmountDetails invoice={invoice} /> : null }

      <Invoice
        invoice={invoice}
      />
    </div>
  )
}

export default Billing
